import TurndownService from 'turndown';
import DOMPurify from 'dompurify';
import { gfm, tables, strikethrough } from 'turndown-plugin-gfm';
import { stripHtml } from '@bignerve/ui-utils';
import { marked } from 'marked';
import { truncate, memoize } from 'lodash';
import { parseVideoUrl } from '/app/utils/video-url';

// Extract main domain (ignore subdomains)
const getMainDomain = (hostname) => {
	const parts = hostname.split('.');
	// For domains like "example.com", take last 2 parts
	return parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
};

const mentionModule = {
	extensions: [
		{
			name: 'mention',
			level: 'inline',
			start (line) {
				const index = line.match(/\B@[a-zA-Z0-9_.-]/)?.index;

				return index !== undefined ? index : -1;
			},
			tokenizer(line, tokens) {
				const [current] = tokens;

				if (!this.lexer.options.mention || (current && current.is_link)) {
					return;
				}

				// Match @ followed by valid username characters, but avoid email patterns
				// Updated regex to allow underscores in the middle and after the first character, e.g., thomas_beha
				// Improved regex to support escaped underscores (e.g., @thomas\_beha) and allow underscores in the middle or end
				// This will match @ followed by a valid username, possibly containing escaped underscores
				const match = line.match(/^@((?:[a-zA-Z0-9](?:[a-zA-Z0-9_.\-\\]*[a-zA-Z0-9_\\])?)|[a-zA-Z0-9])/);

				if (match) {
					const [mention, rawHandle] = match;
					const handle = rawHandle.replace(/\\/g, '');

					// Check if it looks like an email domain part
					if (handle.match(/\.[a-zA-Z]{2,}$/)) {
						return false;
					}

					return {
						type: 'mention',
						raw: mention,
						handle: handle,
						href: `/${handle}`,
						text: `@${handle}`,
					}
				}

				return false;
			},
			renderer(token) {
				if (token.is_link) {
					return;
				}

				return `<a href="${token.href}" title="${token.raw}" class="not-prose font-semibold" rel="noopener noreferrer" target="_blank">${token.text}</a>`;
			},
		}
	],

	walkTokens(token) {
		if (token.type === 'link') {
			token?.tokens.forEach((t) => {
				t.is_link = true
			})
		}
	},
};

const unfurlModule = {
	extensions: [
		{
			name: 'unfurl',
			level: 'inline',
			start(line) {
				return line.indexOf('https://') || 0;
			},
			tokenizer(line) {
				if (!this.lexer.options.unfurl) {
					return;
				  }

				  if (!line.startsWith('http')) {
					return;
				  }

				  const [href] = line.split(' ');
				  const parsed = parseVideoUrl(href);

				  if (parsed) {
					const wrapper = document.createElement('div');
					wrapper.classList.add(
						'rounded-md', 'overflow-hidden', 'shadow-md', 'bg-black',
						'aspect-image', 'w-full', 'mt-4',
					);

					const iframe = document.createElement('iframe');
					iframe.src = parsed.src;
					iframe.frameborder = 0;
					iframe.width = '100%';
					iframe.height = '100%';
					iframe.style.display = 'inline';
					iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
					iframe.allowfullscreen = true;

					wrapper.appendChild(iframe);

					this.lexer.tokens.push({
					  block: true,
					  pre: '', // No pre tag needed
					  type: 'html', // Use container type
					  raw: wrapper.outerHTML, // Add the wrapper's outerHTML
					  text: wrapper.outerHTML, // Add the wrapper's outerHTML
					});
				  }
			},
		},
	],
};

const linkModule = {
	renderer: {
		link({ href, tokens }) {
			const text = this.parser.parseInline(tokens);
			const link = href.replace(/\\/g, '');

			// Check if it's an external link and if it's to a different domain
			let target = '';
			if (link.startsWith('http')) {
				try {
					const linkUrl = new URL(link);
					const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';

					const currentMainDomain = getMainDomain(currentHostname);
					const linkMainDomain = getMainDomain(linkUrl.hostname);

					// Only open in new tab if it's a different main domain
					target = linkMainDomain !== currentMainDomain ? '_blank' : '';
				} catch (e) {
					// If URL parsing fails, treat as external link
					target = '_blank';
				}
			}

			const title = link.startsWith('http') ? text.replace(/\\/g, '') : text;

			// if the title is a link, then we need to truncate the text
			const label = title.startsWith('http') ? truncate(text, { length: 80, separator: /\//, omission: '...' }) : title;

			return target
				? `<a href="${link}" target="${target}" title="${title}" rel="noopener noreferrer">${label}</a>`
				: `<a href="${link}" title="${title}">${label}</a>`;
		}
	}
};

const blockquoteModule = {
	renderer: {
		blockquote({ tokens }) {
			// Parse tokens to HTML and remove wrapping <p> tags
			const body = this.parser.parse(tokens)
				.replace(/^<p[^>]*>\s*(.*?)\s*<\/p>$/gmi, '$1')
				.replace(/<\/p>\s*<p[^>]*>/gmi, '<br><br>');

			return `<blockquote>${body}</blockquote>\n`;
		}
	}
};

marked.use(unfurlModule);
marked.use(mentionModule);
marked.use(linkModule);
marked.use(blockquoteModule);

const turndownService = new TurndownService();

turndownService.use(gfm);
turndownService.use(tables);
turndownService.use(strikethrough);

turndownService.addRule('strikethrough', {
	filter: ['del', 's', 'strike'],
	replacement(content) {
		return `~~${content}~~`;
	},
});

turndownService.addRule('heading', {
	filter: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
	replacement(content, node) {
		const level = Number(node.nodeName.charAt(1));

		return `${'#'.repeat(level === 1 ? 2 : level)} ${content}`;
	},
});

// DOMPurify configuration
const SANITIZER_CONFIG = {
    ALLOWED_TAGS: ['br', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'del', 'a', 'blockquote', 's', 'b', 'div', 'iframe', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'title', 'src', 'width', 'height', 'frameborder', 'allowfullscreen', 'allow', 'style'],
};

// Initialize DOMPurify with config
DOMPurify.setConfig(SANITIZER_CONFIG);

// Create a memoized sanitizer function
const sanitize = memoize((content) => {
    if (!content) return '';
    return DOMPurify.sanitize(content, SANITIZER_CONFIG).trim();
});

// Helper function to preprocess HTML content
const preprocessHtml = (content) => {
    if (!content) return '';
    return content
		// It's important to replace the double spaces with <br>
        .replace(/\s{2,}\n{2}/g, '\n\n<br>\n\n')
		// Replace < and > with &lt; and &gt; to avoid hiding content
        .replace(/<([^>]*?)>/g, '&lt;$1&gt;')
        .replace(/&lt;(br|p|h[1-6]|ul|ol|li|strong|em|del|a|blockquote)( [^>]*)?&gt;/gi, '<$1$2>')
        .replace(/&lt;\/(br|p|h[1-6]|ul|ol|li|strong|em|del|a|blockquote)&gt;/gi, '</$1>')
};

// Main conversion functions
export const toHTML = memoize((content, { inline, ...options } = {}) => {
    const preprocessed = preprocessHtml(content);
    if (!preprocessed) return '';

    const htmlCode = marked[inline ? 'parseInline' : 'parse'](preprocessed, {
        ...options,
        gfm: true,
        breaks: true,
    })

    return sanitize(htmlCode)
		// remove all newlines
		.replace(/\n/g, '')
		// replace all &nbsp; with a space
		.replace(/&nbsp;/g, ' ');
}, (...args) => JSON.stringify(args));

export const toMarkdown = (content) => {
    if (!content) return '';

    return turndownService.turndown(sanitize(content));
};

export const toRaw = (content) => {
    return stripHtml(toHTML(content, { sanitize: true })).trim();
};

export const useMarkdown = () => ({
    methods: { toHTML, toMarkdown, toRaw, stripHtml }
});
